import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { FormField } from '@/shared/components/ui/FormField';
import { apiPost } from '@/lib/api';

interface CreditAdjustModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

export const CreditAdjustModal = ({ isOpen, onClose, companyId }: CreditAdjustModalProps) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => apiPost(`/companies/${companyId}/credits/adjust`, {
      type,
      amount: parseInt(amount, 10),
      reason,
    }),
    onSuccess: () => {
      toast.success(type === 'credit' ? 'Créditos adicionados com sucesso' : 'Créditos removidos com sucesso');
      queryClient.invalidateQueries({ queryKey: ['admin-company-credits', companyId] });
      handleClose();
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error?.response?.data?.message || 'Erro ao ajustar créditos');
    },
  });

  const handleClose = () => {
    setType('credit');
    setAmount('');
    setReason('');
    onClose();
  };

  const isValid = parseInt(amount, 10) > 0 && reason.trim().length >= 3;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-xl shadow-2xl p-6"
        style={{
          backgroundColor: '#ffffff',
          color: '#1e293b',
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
          '--theme-button-primary': '#8b5cf6',
          '--theme-button-primary-text': '#ffffff',
          '--theme-button-primary-hover': '#7c3aed',
          '--theme-success-color': '#10b981',
          '--theme-border': 'rgba(0, 0, 0, 0.06)',
        } as React.CSSProperties}
      >
        <h2 className="text-lg font-semibold mb-4" style={{ color: '#1e293b' }}>
          Ajustar Créditos
        </h2>

        <div className="space-y-4">
          <FormField label="Tipo de ajuste" required>
            <Select
              value={type}
              onChange={(v) => setType(v as 'credit' | 'debit')}
              options={[
                { label: 'Adicionar créditos', value: 'credit' },
                { label: 'Remover créditos', value: 'debit' },
              ]}
            />
          </FormField>

          <FormField label="Quantidade" required>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ex: 1000"
              min={1}
              max={1000000}
            />
          </FormField>

          <FormField label="Motivo" required>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Descreva o motivo do ajuste..."
              rows={3}
            />
          </FormField>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant={type === 'credit' ? 'success' : 'error'}
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!isValid}
          >
            {type === 'credit' ? 'Adicionar' : 'Remover'}
          </Button>
        </div>
      </div>
    </div>
  );
};
